import React, { useState, useEffect } from 'react';
import {
  IoSearch,
  IoAdd,
  IoPaperPlane,
  IoAttach,
  IoCheckmark,
  IoChatbubbles,
  IoArrowForward,
  IoFileTray,
  IoConstruct,
  IoChevronBack,
  IoChevronForward,
} from 'react-icons/io5';
import DemoBanner from '@/components/DemoBanner';
import './Messages.css';

interface Conversation {
  id: string;
  name: string;
  role: string;
  lastMessage: string;
  timeAgo: string;
  unreadCount?: number;
  isOnline?: boolean;
  avatar?: string;
  avatarType?: 'image' | 'gradient';
  gradientColors?: string;
}

const Messages: React.FC = () => {
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'unread'>('all');
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [messageText, setMessageText] = useState('');
  const [isConversationListCollapsed, setIsConversationListCollapsed] = useState(false);

  // Debug: Log component mount
  useEffect(() => {
    console.log('Messages component mounted');
  }, []);

  const [conversations] = useState<Conversation[]>([
    {
      id: '1',
      name: 'Mr. David Thompson',
      role: 'School Counselor',
      lastMessage: 'I reviewed your Stanford essay and have some...',
      timeAgo: '2h ago',
      unreadCount: 2,
      isOnline: true,
      avatar: 'https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-2.jpg',
      avatarType: 'image',
    },
    {
      id: '2',
      name: 'AI Assistant',
      role: 'College Application Help',
      lastMessage: 'Here are some colleges that match your profile...',
      timeAgo: '1d ago',
      avatarType: 'gradient',
      gradientColors: 'from-blue-500 to-purple-600',
    },
    {
      id: '3',
      name: 'AI Assistant',
      role: 'Essay Review Query',
      lastMessage: 'Your essay demonstrates strong narrative voice...',
      timeAgo: '3d ago',
      avatarType: 'gradient',
      gradientColors: 'from-green-500 to-teal-600',
    },
    {
      id: '4',
      name: 'AI Assistant',
      role: 'Application Timeline',
      lastMessage: "Based on your deadlines, here's a suggested...",
      timeAgo: '5d ago',
      avatarType: 'gradient',
      gradientColors: 'from-orange-500 to-red-600',
    },
    {
      id: '5',
      name: 'Ms. Jennifer Lee',
      role: 'College Advisor',
      lastMessage: 'Great job on your MIT application! Just a few...',
      timeAgo: '1w ago',
      avatar: 'https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-8.jpg',
      avatarType: 'image',
    },
  ]);

  const handleSendMessage = () => {
    if (messageText.trim()) {
      console.log('Sending message:', messageText);
      setMessageText('');
      // In real app, this would send the message to the backend
    }
  };

  const handleQuickSuggestion = (suggestion: string) => {
    setMessageText(suggestion);
  };

  const handleNewConversation = () => {
    console.log('Starting new conversation');
    setSelectedConversation(null);
  };

  const filteredConversations = selectedFilter === 'unread'
    ? conversations.filter(c => c.unreadCount && c.unreadCount > 0)
    : conversations;

  return (
    <div className="messages-page dark:bg-gray-900">
      <DemoBanner />
      {/* Messaging Interface */}
      <div className="messaging-interface">
        {/* Conversation List */}
        <div className={`conversation-list dark:bg-gray-800 dark:border-gray-700 ${isConversationListCollapsed ? 'collapsed' : ''}`}>
          <div className="conversation-list-header dark:border-gray-700">
            {!isConversationListCollapsed && (
              <>
                <h2 className="conversation-list-title dark:text-gray-100">Conversations</h2>
                <div className="search-container dark:bg-gray-700">
                  <IoSearch className="search-icon dark:text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search conversations..."
                    className="search-input dark:text-gray-100 dark:placeholder-gray-400"
                  />
                </div>
                <div className="filter-buttons">
                  <button
                    className={`filter-btn dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 ${selectedFilter === 'all' ? 'active dark:!bg-blue-600 dark:!text-white' : ''}`}
                    onClick={() => setSelectedFilter('all')}
                  >
                    All
                  </button>
                  <button
                    className={`filter-btn dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 ${selectedFilter === 'unread' ? 'active dark:!bg-blue-600 dark:!text-white' : ''}`}
                    onClick={() => setSelectedFilter('unread')}
                  >
                    Unread
                  </button>
                </div>
              </>
            )}
            <button
              className="collapse-toggle-btn dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              onClick={() => setIsConversationListCollapsed(!isConversationListCollapsed)}
              title={isConversationListCollapsed ? 'Expand' : 'Collapse'}
            >
              {isConversationListCollapsed ? <IoChevronForward /> : <IoChevronBack />}
            </button>
          </div>

          <div className="conversations-container">
            {filteredConversations.map((conversation) => (
              <div
                key={conversation.id}
                className={`conversation-item dark:border-gray-700 dark:hover:bg-gray-700 ${selectedConversation === conversation.id ? 'selected dark:!bg-gray-700' : ''}`}
                onClick={() => setSelectedConversation(conversation.id)}
              >
                <div className="conversation-content">
                  <div className="avatar-container">
                    {conversation.avatarType === 'image' && conversation.avatar ? (
                      <>
                        <img
                          src={conversation.avatar}
                          alt={conversation.name}
                          className="avatar-image"
                        />
                        {conversation.isOnline && <span className="online-indicator" />}
                      </>
                    ) : (
                      <div
                        className="avatar-gradient"
                        style={{
                          background:
                            conversation.gradientColors === 'from-blue-500 to-purple-600'
                              ? 'linear-gradient(to bottom right, #3b82f6, #9333ea)'
                              : conversation.gradientColors === 'from-green-500 to-teal-600'
                              ? 'linear-gradient(to bottom right, #10b981, #14b8a6)'
                              : conversation.gradientColors === 'from-orange-500 to-red-600'
                              ? 'linear-gradient(to bottom right, #f97316, #dc2626)'
                              : 'linear-gradient(to bottom right, #3b82f6, #9333ea)',
                        }}
                      >
                        <IoConstruct className="avatar-icon" />
                      </div>
                    )}
                  </div>
                  {!isConversationListCollapsed && (
                    <div className="conversation-details">
                      <div className="conversation-header">
                        <h3 className="conversation-name dark:text-gray-100">{conversation.name}</h3>
                        <span className="conversation-time dark:text-gray-400">{conversation.timeAgo}</span>
                      </div>
                      <p className="conversation-role dark:text-gray-400">{conversation.role}</p>
                      <p className="conversation-preview dark:text-gray-300">{conversation.lastMessage}</p>
                      {conversation.unreadCount && (
                        <div className="unread-badge-container">
                          <span className="unread-badge">{conversation.unreadCount}</span>
                          <span className="unread-text">Unread</span>
                        </div>
                      )}
                    </div>
                  )}
                  {isConversationListCollapsed && conversation.unreadCount && (
                    <span className="unread-badge-collapsed">{conversation.unreadCount}</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="new-conversation-section dark:border-gray-700">
            <button 
              className={`new-conversation-btn dark:bg-blue-600 dark:hover:bg-blue-700 ${isConversationListCollapsed ? 'collapsed' : ''}`}
              onClick={handleNewConversation}
              title={isConversationListCollapsed ? 'New Conversation' : ''}
            >
              <IoAdd />
              {!isConversationListCollapsed && <span>New Conversation</span>}
            </button>
          </div>
        </div>

        {/* Main Messaging Area */}
        <div className="main-messaging-area dark:bg-gradient-to-br dark:from-gray-900 dark:to-gray-800">
          {!selectedConversation ? (
            <div className="empty-state">
              <div className="empty-state-content-wrapper">
                <div className="empty-state-content">
                <div className="empty-state-icon-container">
                  <IoChatbubbles className="empty-state-icon" />
                </div>
                <h2 className="empty-state-title dark:text-gray-100">Ask us anything</h2>
                <p className="empty-state-description dark:text-gray-300">
                  We'll try our best to answer your question and if we can't we'll help pass on your question to your counselor!
                </p>

                <div className="features-card dark:bg-gray-800 dark:border dark:border-gray-700">
                  <div className="features-list">
                    <div className="feature-item">
                      <div className="feature-check dark:bg-blue-600">
                        <IoCheckmark className="check-icon" />
                      </div>
                      <p className="dark:text-gray-200">Find what you're looking for in the app</p>
                    </div>
                    <div className="feature-item">
                      <div className="feature-check dark:bg-blue-600">
                        <IoCheckmark className="check-icon" />
                      </div>
                      <p className="dark:text-gray-200">Answer questions about colleges</p>
                    </div>
                    <div className="feature-item">
                      <div className="feature-check dark:bg-blue-600">
                        <IoCheckmark className="check-icon" />
                      </div>
                      <p className="dark:text-gray-200">Plan your next moves</p>
                    </div>
                  </div>

                  <button className="counselor-button dark:from-blue-600 dark:to-blue-700">
                    <img
                      src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-2.jpg"
                      alt="Counselor"
                      className="counselor-avatar"
                    />
                    <span>Message my counselor directly</span>
                    <IoArrowForward className="arrow-icon" />
                  </button>

                  <p className="ai-hint dark:text-gray-400">Or start typing below to chat with our AI assistant</p>
                </div>

                {/* Quick Suggestions */}
                <div className="quick-suggestions">
                  <p className="suggestions-title dark:text-gray-300">Quick suggestions:</p>
                  <div className="suggestions-grid">
                    {[
                      "What's my application status?",
                      'Help with my essay',
                      'College recommendations',
                      'Upcoming deadlines',
                    ].map((suggestion) => (
                      <button
                        key={suggestion}
                        className="suggestion-button dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-700"
                        onClick={() => handleQuickSuggestion(suggestion)}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              </div>
              
              {/* Message Input Area - Always visible at bottom */}
              <div className="message-input-area dark:bg-gray-800 dark:border dark:border-gray-700">
                <div className="input-container">
                  <button className="attach-button dark:text-gray-300 dark:hover:bg-gray-700">
                    <IoAttach className="attach-icon" />
                  </button>
                  <textarea
                    rows={1}
                    placeholder="Type your message here..."
                    className="message-textarea dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                    value={messageText}
                    onChange={(e) => {
                      setMessageText(e.target.value);
                      // Auto-resize textarea
                      e.target.style.height = 'auto';
                      e.target.style.height = `${Math.min(e.target.scrollHeight, 80)}px`;
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                  />
                  <button
                    className="send-button dark:bg-blue-600 dark:hover:bg-blue-700"
                    onClick={handleSendMessage}
                    disabled={!messageText.trim()}
                  >
                    <IoPaperPlane className="send-icon" />
                  </button>
                </div>
                <div className="input-footer">
                  <button className="attach-file-link dark:text-gray-400 dark:hover:text-blue-400">
                    <IoFileTray className="file-icon" />
                    <span>Attach file</span>
                  </button>
                  <p className="input-hint dark:text-gray-400">Press Enter to send</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="active-conversation">
              <div className="conversation-header-bar dark:bg-gray-800 dark:border-gray-700">
                <h3 className="active-conversation-name dark:text-gray-100">
                  {conversations.find(c => c.id === selectedConversation)?.name}
                </h3>
              </div>
              <div className="messages-container">
                <p className="no-messages dark:text-gray-400">Select a conversation to view messages</p>
              </div>
              <div className="message-input-area dark:bg-gray-800 dark:border dark:border-gray-700">
                <div className="input-container">
                  <button className="attach-button dark:text-gray-300 dark:hover:bg-gray-700">
                    <IoAttach className="attach-icon" />
                  </button>
                  <textarea
                    rows={1}
                    placeholder="Type your message here..."
                    className="message-textarea dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                    value={messageText}
                    onChange={(e) => {
                      setMessageText(e.target.value);
                      // Auto-resize textarea
                      e.target.style.height = 'auto';
                      e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                  />
                  <button
                    className="send-button dark:bg-blue-600 dark:hover:bg-blue-700"
                    onClick={handleSendMessage}
                    disabled={!messageText.trim()}
                  >
                    <IoPaperPlane className="send-icon" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Messages;
